
import Raf from "quark-raf";
import { html, body } from '../utils/environment';
import { clamp, map } from "../utils/maths";
import { getIsTouchable } from "../utils/touchable";

const cartModalSwipeMixin = {
    created() {
        // Environment
        this.isTouchable = getIsTouchable()
                
        // Drag
        this.dragState = {
            isSwipeRafPlaying: false,
            isDragAvailable: false,
            isDragging: false,
            isExpanded: false,
            isOpen: false,
            isClose: true
        }

        this.drag = {
            oldY: 0,
            y: 0,
            startY: 0,
            smoothY: 0,
            threshold: 100,
        }

        this.dragStep = {
            openY: 0,
            closeY: 0,
            //preOpenY: 0,
        }
    },

    mounted() {
        this.bindEvents()

        // If isTouchable change styles
        if (this.isTouchable && window.isMobile) {
            html.classList.add('has-swipe-cart-available')
        }
    },

    beforeDestroy() {
        this.stopSwipeRaf()
        this.unbindEvents()
    },

    methods: {
        /////////////////////////
        // Binding
        bindEvents() {
            this.onResize = this.onResize.bind(this)
            window.addEventListener('resize', this.onResize)
        },

        unbindEvents() {
            window.removeEventListener('resize', this.onResize)
        },

        /////////////////////////
        // Swipe Cart Steps
        openSwipeCart() {
            if (!this.isTouchable || !window.isMobile || this.dragState.isOpen || !this.dragState.isClose ) return

            // Get metrics
            this.computeDragMetrics()

            this.$nextTick(() => {
                // Start raf
                this.playSwipeRaf()

                // Set expanded state
                this.expandSwipeCart()

                // Set state values
                this.dragState.isDragAvailable = true
                this.dragState.isOpen = true

                // Styles
                this.$refs.cartContent.scrollTop = 0
                
                requestAnimationFrame(() => {
                    html.classList.add('has-swipe-cart-open')
                    this.moduleInstance?.onOpenSwipeModal?.()
                })
            })
        },

        expandSwipeCart() {
            if (!this.isTouchable || !window.isMobile) return

            // Set state
            this.dragState.isExpanded = true

            // Anime
            this.drag.y = this.dragStep.openY;
        },
        
        closeSwipeCart() {
            if (!this.dragState.isOpen || this.dragState.isClose) return

            // Get metrics (if new item is added to the cart when it's open)
            const $cartInner = this.$refs.cartInner
            const cartInnerHeight = $cartInner.offsetHeight
            this.dragStep.closeY = cartInnerHeight
            this.dragStep.openY = 0

            // Set state
            this.dragState.isDragAvailable = false
            this.dragState.isDragging = false;
            this.dragState.isExpanded = false;
            this.dragState.isOpen = false

            // Anime
            this.drag.y = this.dragStep.closeY
        },

        onCloseAnimationEnded() {
            html.classList.remove('has-cart-open')
            html.classList.remove('has-swipe-cart-open')
            this.moduleInstance?.onCloseSwipeModal?.()
        },

        /////////////////////////
        // Events
        onResize() {
            this.computeDragMetrics(true)
        },

        onDragStart(event) {
            if (!this.dragState.isDragAvailable || !this.dragState.isOpen || this.dragState.isClose) return

            this.dragState.isDragging = true;

            this.pointerEvent = event.type !== 'mousedown' ? (event.touches[0] || event.changedTouches[0]) : event;

            this.drag.oldY = this.drag.y;

            this.drag.startY = this.pointerEvent.clientY;

            this.startTime = new Date().getTime()
        },

        onDrag(event) {
            if(!this.dragState.isDragging) return;

            this.pointerEvent = event.type !== 'mousemove' ? (event.touches[0] || event.changedTouches[0]) : event;

            this.drag.y = this.drag.oldY + (this.pointerEvent.clientY - this.drag.startY);
            
            this.drag.y = clamp(this.drag.y,  this.dragStep.openY, this.dragStep.closeY)
        },

        onDragEnd(event) {
            if(!this.dragState.isDragging) return;

            this.dragState.isDragging = false;

            this.dragDistY = this.pointerEvent.clientY - this.drag.startY

            
            if (!this.dragState.isExpanded && this.dragDistY > this.drag.threshold) {
                this.closeSwipeCart()
            }
            else if (this.dragState.isExpanded && this.dragDistY > this.drag.threshold) {
                this.closeSwipeCart()
            }
            else if (this.dragState.isExpanded && this.dragDistY < this.drag.threshold) {
                this.expandSwipeCart()
            }
            else if (!this.dragState.isExpanded && this.dragDistY < this.drag.threshold) {
                this.expandSwipeCart()
            }
        },

        /////////////////////////
        // Calc
        computeDragMetrics(resize = false) {
            const $cartInner = this.$refs.cartInner
            const cartInnerHeight = $cartInner.offsetHeight

            // Common metrics
            this.dragStep.closeY = cartInnerHeight
            this.dragStep.openY = 0

            // Resize metrics
            if (resize) {
                if (this.dragState.isExpanded) {
                    this.drag.y = 0 
                }
                
            } else {
                this.$refs.cartInner.style.transform = `translate3d(0, ${cartInnerHeight}px, 0)`;
                this.drag.y = this.drag.smoothY = cartInnerHeight
            }
        },

        /////////////////////////
        // Animations
        updateSwipeBackground(y, minY, maxY) {
            if (!this.$refs.cartBackground) return
            this.$refs.cartBackground.style.opacity = map(y, minY, maxY, 0, 1)
        },
        
        updateSwipeTransform(y) {
            if (!this.$refs.cartInner) return
            this.$refs.cartInner.style.transform = `translate3d(0, ${y}px, 0)`;
        },

        /////////////////////////
        // Render
        playSwipeRaf() {
            if (this.dragState.isSwipeRafPlaying) return
            this.dragState.isSwipeRafPlaying = true
            this.updateSwipeRaf = this.updateSwipeRaf.bind(this)
            Raf.add(this.updateSwipeRaf);
        },

        stopSwipeRaf() {
            if (!this.dragState.isSwipeRafPlaying) return
            this.dragState.isSwipeRafPlaying = false
            Raf.remove(this.updateSwipeRaf);
        },

        updateSwipeRaf() {
            if (!this.dragState.isDragAvailable && this.drag.smoothY > this.dragStep.closeY - 1) {
                this.stopSwipeRaf()
            }

            this.drag.smoothY += (this.drag.y - this.drag.smoothY) * 0.2;
            this.drag.smoothY = clamp(this.drag.smoothY, this.dragStep.openY + .5, this.dragStep.closeY)

            this.updateSwipeTransform(this.drag.smoothY)
            this.updateSwipeBackground(this.drag.smoothY, this.dragStep.closeY, this.dragStep.openY)

            if (this.dragState.isOpen && this.drag.smoothY <= this.dragStep.openY + .5) {
                this.dragState.isClose = false
            }

            if (!this.dragState.isOpen && this.drag.smoothY >= this.dragStep.closeY - 1) {
                this.onCloseAnimationEnded()
                this.dragState.isClose = true
            }
        },
    }
}

export default cartModalSwipeMixin;
